import React, { useCallback, useEffect, useRef, useState } from 'react';
import { animationUtils } from '@prisjakt/ui-base';
import { IconChevronLeft, IconChevronRight } from '@prisjakt/ui/icon';
import { Spinner } from 'src/components/spinner';
import { EPSILON, TRANSITION_TIME_MAX, TRANSITION_TIME_MIN } from './constants';
import {
  HorizontalScrollContainer,
  Inner,
  LeftArrow,
  List,
  Loader,
  RightArrow,
} from './styles';

interface HorizontalScrollProps {
  itemWidth?: number;
  loadingHeight?: number;
  loading?: boolean;
  showArrows?: boolean | 'always';
  children: React.ReactNode;
  className?: string;
  // Adds negative margin on mobile screens to put content flush
  // with page. Used mainly on landing page.
  negativeMargin?: boolean;
}

function useHorizontalScroll(props: Omit<HorizontalScrollProps, 'itemWidth'>) {
  const innerContainerRef = useRef<HTMLUListElement | null>();
  const outerContainerRef = useRef<HTMLDivElement | null>();

  const [hasStartArrow, setHasStartArrow] = useState(props.showArrows ?? true);
  const [hasEndArrow, setHasEndArrow] = useState(props.showArrows ?? true);

  const getWidths = () => {
    if (!outerContainerRef.current || !innerContainerRef.current)
      return { scrollWidth: 0, clientWidth: 0, scrollableWidth: 0 };

    return {
      clientWidth: innerContainerRef.current.clientWidth,
      scrollWidth: innerContainerRef.current.scrollWidth,
      scrollableWidth: Math.max(
        0,
        innerContainerRef.current.scrollWidth -
          innerContainerRef.current.clientWidth
      ),
    };
  };

  const toggleArrows = useCallback(
    (scrollLeft: number) => {
      const { scrollWidth, scrollableWidth, clientWidth } = getWidths();
      const scrollRight = scrollableWidth - scrollLeft;

      setHasStartArrow(
        props.showArrows === 'always' ||
          (scrollWidth > clientWidth && scrollLeft > 0)
      );

      setHasEndArrow(
        props.showArrows === 'always' ||
          (scrollWidth > clientWidth && scrollRight > 0)
      );
    },
    [props.showArrows]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    toggleArrows((e.target as HTMLElement)?.scrollLeft ?? 0);
  };

  const scrollTo = ({ from, to }: { from: number; to: number }) => {
    if (from === to) return;

    const distanceNormalize = Math.abs(to - from) / TRANSITION_TIME_MIN;
    const duration = Math.max(
      TRANSITION_TIME_MIN,
      Math.min(TRANSITION_TIME_MAX, TRANSITION_TIME_MIN * distanceNormalize)
    );

    animationUtils.tween({
      from,
      to,
      duration,
      onUpdate: (value: number) => {
        if (outerContainerRef.current) {
          outerContainerRef.current.scrollLeft = Math.round(value);
        }
      },
    });
  };

  const togglePage = (direction: 'prev' | 'next') => {
    const scrollLeft = outerContainerRef.current?.scrollLeft ?? 0;
    const { scrollableWidth, clientWidth } = getWidths();

    function round(value: number, rounded: number) {
      return Math.abs(rounded - value) <= EPSILON ? rounded : value;
    }

    // This needs to match margin-left offset on li:not(:first-child)
    // so that it moves a complete "set" of items each click
    const marginOffset = 12;

    scrollTo({
      from: scrollLeft,
      to:
        direction === 'prev'
          ? Math.max(0, round(scrollLeft - clientWidth - marginOffset, 0))
          : Math.min(
              scrollableWidth,
              round(scrollLeft + clientWidth + marginOffset, scrollableWidth)
            ),
    });
  };

  useEffect(() => {
    toggleArrows(0);
  }, [toggleArrows]);

  return {
    innerContainerRef,
    outerContainerRef,
    handleScroll,
    togglePage,
    hasStartArrow,
    hasEndArrow,
  };
}

export const HorizontalScroll: React.FC<HorizontalScrollProps> = ({
  itemWidth,
  negativeMargin,
  ...props
}) => {
  const {
    innerContainerRef,
    outerContainerRef,
    handleScroll,
    togglePage,
    hasStartArrow,
    hasEndArrow,
  } = useHorizontalScroll(props);

  return (
    <HorizontalScrollContainer
      data-test="HorizontalScroll"
      className={props.className}
      negativeMargin={negativeMargin}
    >
      <Inner
        ref={div => {
          outerContainerRef.current = div;
        }}
        onScroll={handleScroll}
      >
        {props.showArrows !== false && (
          <>
            {hasStartArrow && (
              <LeftArrow onClick={() => togglePage('prev')}>
                <IconChevronLeft />
              </LeftArrow>
            )}
            {hasEndArrow && (
              <RightArrow onClick={() => togglePage('next')}>
                <IconChevronRight />
              </RightArrow>
            )}
          </>
        )}
        <List
          ref={div => {
            innerContainerRef.current = div;
          }}
        >
          {React.Children.map(props.children, (child, i) => (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              style={{ flex: itemWidth ? `0 0 ${itemWidth}px` : undefined }}
            >
              {child}
            </li>
          ))}
          {props.loading && (
            <Loader style={{ lineHeight: `${props.loadingHeight}px` }}>
              <Spinner position="none" isActive />
            </Loader>
          )}
        </List>
      </Inner>
    </HorizontalScrollContainer>
  );
};

export default HorizontalScroll;
